// components/masterdata/ZustaendeListe.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const ZustaendeListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [zustaende, setZustaende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [kategorien, setKategorien] = useState([]);

  const [newZustand, setNewZustand] = useState({
    name: "",
    kategorie: "",
    order: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchKategorien = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/kategorien`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      setKategorien(data);
    } catch (err) {
      console.error("Fehler beim Laden der Kategorien:", err);
    }
  }, [accessToken]);

  const fetchZustaende = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/zustaende`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Zustaende');
      }

      const data = await response.json();
      setZustaende(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/zustaende/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchZustaende();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diesen Zustand wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/zustaende/${id}/`, {
        method: "DELETE",
      });
      fetchZustaende(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddZustaende = async () => {
    if (!newZustand.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/zustaende/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newZustand)
      });

      if (response.ok) {
        setNewZustand({ name: "", kategorie: "", order: "" });
        fetchZustaende();
      } else {
        alert('Fehler beim Hinzufügen');
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchZustaende();
    fetchKategorien();  // Dropdown Kategorie im Editmodus
  }, [accessToken, fetchZustaende, fetchKategorien]);

  if (loading) return <div>Lade Zustaende...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Zustände verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neuer Zustand..."
            value={newZustand.name}
            onChange={(e) => setNewZustand({ ...newZustand, name: e.target.value })}
          />
          <select
            className="form-select"
            value={newZustand.kategorie}
            onChange={(e) => setNewZustand({ ...newZustand, kategorie: e.target.value })}
          >
            <option value="">Bitte wählen</option>
            {kategorien.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
          <input
            className="form-control"
            type="text"
            placeholder="Sortierung"
            value={newZustand.order}
            onChange={(e) => setNewZustand({ ...newZustand, order: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddZustaende} disabled={editMode}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Kategorie</th>
              <th>Sortierung</th>
            </tr>
          </thead>
          <tbody>
            {zustaende.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Kategorie */}
                <td>
                  {editMode ? (
                    <select
                      className="form-select"
                      value={editData[kat.id]?.kategorie || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          [kat.id]: { ...editData[kat.id], kategorie: e.target.value }
                        })
                      }
                    >
                      <option value="">Bitte wählen</option>
                      {kategorien.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    kat.kategorie_name || kat.kategorie
                  )}
                </td>

                {/* Order */}
                <td>
                  {editMode ? (
                    <input
                      type="number"
                      className="form-control"
                      value={editData[kat.id]?.order || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], order: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.order
                  )}
                </td>

              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {editMode ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setEditMode(false);
                setEditData({});
              }}
            >
              Abbrechen
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/objekte")}
            >
              Zurück
            </button>
          )}

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              zustaende.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  order: k.order,
                  kategorie: k.kategorie
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ZustaendeListe;
